/**
 * Persists the search-page service location choice so signup can prefill
 * `protectedData.serviceAddress` (same shape as FieldLocationAutocompleteInput).
 */

import { isOriginInUse } from './search';

const STORAGE_KEY = 'fyk_search_service_address_draft_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @param {*} value - autocomplete value: { search?, selectedPlace?: { address, origin, bounds } }
 * @param {object} [config] - app config; when set, origin is required only if isOriginInUse(config)
 * @returns {boolean}
 */
export function isCompleteServiceAddressDraft(value, config) {
  if (!value || !value.selectedPlace) {
    return false;
  }
  const { address: placeAddress, origin, bounds } = value.selectedPlace;
  const address = placeAddress || value.search;
  if (!address || !bounds) {
    return false;
  }
  const needOrigin = !config || isOriginInUse(config);
  if (needOrigin && !origin) {
    return false;
  }
  return true;
}

/**
 * @param {*} value
 * @param {object} [config]
 */
export function storeSearchServiceAddressDraft(value, config) {
  if (typeof window === 'undefined' || !value || !isCompleteServiceAddressDraft(value, config)) {
    return;
  }
  try {
    const address = value.selectedPlace.address || value.search;
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        search: value.search || address || '',
        selectedPlace: {
          address,
          origin: value.selectedPlace.origin,
          bounds: value.selectedPlace.bounds,
        },
        storedAt: Date.now(),
      })
    );
  } catch (e) {
    // Quota or private mode
  }
}

/**
 * @returns {{ search: string, selectedPlace: object } | null}
 */
export function readSearchServiceAddressDraft() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw);
    if (!o || typeof o.storedAt !== 'number') {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - o.storedAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const draft = {
      search: o.search || '',
      selectedPlace: o.selectedPlace || null,
    };
    return isCompleteServiceAddressDraft(draft) ? draft : null;
  } catch (e) {
    return null;
  }
}

export function clearSearchServiceAddressDraft() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Pre-fill hidden `prot_serviceAddress` on signup / IdP confirm when a search-page draft exists.
 *
 * @param {{ userType?: string }} baseInitialValues
 * @returns {object}
 */
export function mergeServiceAddressDraftIntoSignupInitialValues(baseInitialValues) {
  if (typeof window === 'undefined') {
    return baseInitialValues;
  }
  if (baseInitialValues?.userType !== 'customer-main') {
    return baseInitialValues;
  }
  const draft = readSearchServiceAddressDraft();
  if (!draft) {
    return baseInitialValues;
  }
  return { ...baseInitialValues, prot_serviceAddress: draft };
}
